import { Injectable } from '@angular/core';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { from, Observable, of, timer } from 'rxjs';
import {
  GeocoderResponse,
  GeocoderStatus,
} from '@models/responses/geocoder-response.model';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import {
  COUNTRY_CODE_TO_CULTURE,
  FALLBACK_CULTURE,
  GEOLOCATION_PERMISSION_FETCH_TIMEOUT,
  GEOLOCATION_PERMISSION_PROMPT_TIMEOUT,
  SECONDS_PER_DAY,
} from '@constants/app.constants';
import { selectUserLocation } from '@selectors/station.selector';
import { CookieStorageService } from '@services/cookie-storage/cookie-storage.service';
import { navigateToNewCulture } from '@actions/navigate.action';
import { WebApiService } from '@services/web-api/web-api.service';

import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { Router } from "@angular/router";

@Injectable({
  providedIn: 'root',
})
export class GeocodeService {
  private readonly COUNTRY_ADDRESS_TYPE = 'country';
  private geocoder: google.maps.Geocoder | null = null;
  private isMapsLoaded = false;

  constructor(
    private httpClient: HttpClient,
    private store: Store,
    private cookieStorageService: CookieStorageService,
    private webApiService: WebApiService,
    private router: Router,
  ) {}

  public loadMaps(): Observable<boolean> {
    if (this.isMapsLoaded) {
      return of(true);
    }

    return this.httpClient
      .jsonp(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyB78LHYyoZW9HAqiaHtc-FV1Ns3avIZpgI&libraries=places&language=en&signature=6GpJHB0yxhL5uDHzzOrFbTcFr7A=',
        'callback',
      )
      .pipe(
        map(() => {
          this.isMapsLoaded = true;
          this.geocoder = this.createGeocoder();
          return true;
        }),
        catchError(() => of(false)),
      );
  }

  // not really testable as the google script must be loaded
  /* istanbul ignore next */
  createGeocoder(): google.maps.Geocoder {
    return new google.maps.Geocoder();
  }

  geocodeLatLng(
    location: google.maps.LatLngLiteral,
  ): Promise<GeocoderResponse> {
    return new Promise((resolve) => {
      this.geocoder?.geocode({ location: location }, (results, status) => {
        const response = new GeocoderResponse(status, results);
        resolve(response);
      });
    });
  }

  /* istanbul ignore next */
  checkPermissions(): Observable<PermissionStatus> {
    return from(this.webApiService.checkPermissions('geolocation'));
  }

  /* istanbul ignore next */
  getCurrentPosition(): Observable<GeolocationPosition | null> {
    const options = {
      enableHighAccuracy: false,
      timeout: GEOLOCATION_PERMISSION_FETCH_TIMEOUT,
      maximumAge: Infinity,
    };
    const currentPosition = this.webApiService.getCurrentPosition(options);
    return from(currentPosition);
  }

  geocoderResultToCountryName(
    firstResult?: google.maps.GeocoderResult,
  ): string {
    if (!firstResult) {
      return '';
    }
    const addresses = firstResult.address_components;
    return (
      addresses.find((address) => {
        return address.types.includes(this.COUNTRY_ADDRESS_TYPE);
      })?.short_name ?? ''
    );
  }

  navigateToGeocodedCulture(): void {
    if (!this.isMapsLoaded) {
      return;
    }
    this.store
      .select(selectUserLocation)
      .pipe(takeUntil(timer(GEOLOCATION_PERMISSION_PROMPT_TIMEOUT)))
      .subscribe((position) => {
        if (!position || !position.byPrompt) {
          return;
        }
        const latlng = {
          lat: Number(position?.lat),
          lng: Number(position?.lon),
        };
        this.geocodeLatLng(latlng).then((response: GeocoderResponse) => {
          if (
            response.status !== GeocoderStatus.OK ||
            !response.results ||
            response.results.length <= 0
          ) {
            return;
          }
          const countryName = this.geocoderResultToCountryName(
            response.results[0],
          );
          const newCulture = COUNTRY_CODE_TO_CULTURE[countryName] ?? FALLBACK_CULTURE;
          this.cookieStorageService.setItem(
            CookieStorageService.cookieCulture,
            newCulture,
            180 * SECONDS_PER_DAY,
          );

          this.store.dispatch(navigateToNewCulture({ newCulture }));
        });
      });
  }
}
